import { defineStore } from 'pinia';
import { createApiV2Client } from '../services/apiV2.js';

const DEFAULT_PAGE_SIZE = 25;
const COMPLETE_PAGE_SIZE = 100;
const RESIDENT_STATUSES = ['ACTIVE', 'LEFT_SITE', 'DECEASED'];

const residentConfig = {
    apiRoot: null,
    fetchImpl: undefined,
    idempotencyKeyFactory: () => {
        if (globalThis.crypto?.randomUUID) return crypto.randomUUID();
        return `resident-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    },
};

export function configureResidentsApi(options = {}) {
    for (const key of ['apiRoot', 'fetchImpl', 'idempotencyKeyFactory']) {
        if (Object.prototype.hasOwnProperty.call(options, key)) {
            residentConfig[key] = options[key];
        }
    }
}

export function normalizeResidentQuery(query = {}) {
    const page = Number(query.page);
    const pageSize = Number(query.pageSize);
    const status = RESIDENT_STATUSES.includes(query.status)
        ? query.status
        : 'ACTIVE';
    const search = String(query.search || '')
        .trim()
        .slice(0, 100);
    return {
        page: Number.isSafeInteger(page) && page > 0 ? page : 1,
        pageSize:
            Number.isSafeInteger(pageSize) && pageSize > 0 && pageSize <= 100
                ? pageSize
                : DEFAULT_PAGE_SIZE,
        status,
        ...(search ? { search } : {}),
        ...(query.linkedToOpenOutbreak === true
            ? { linkedToOpenOutbreak: true }
            : {}),
        ...(Number.isSafeInteger(Number(query.outbreakId)) &&
        Number(query.outbreakId) > 0
            ? { outbreakId: Number(query.outbreakId) }
            : {}),
    };
}

export function quotedVersion(version) {
    const parsed = Number(version);
    if (!Number.isSafeInteger(parsed) || parsed < 1) {
        throw new TypeError('A positive Resident version is required.');
    }
    return `"${parsed}"`;
}

export function buildResidentStatusPayload(form = {}) {
    const effectiveDate = String(form.effectiveDate || '');
    if (!/^\d{4}-\d{2}-\d{2}$/.test(effectiveDate)) {
        throw new TypeError('Resident effective date must use YYYY-MM-DD.');
    }
    const reason = String(form.reason || '').trim();
    return {
        status: form.status,
        effectiveDate,
        ...(reason ? { reason } : {}),
        note: String(form.note || '').trim() || null,
    };
}

export function residentStatusEffectiveDate(
    currentStatus,
    currentEffectiveDate,
    nextStatus
) {
    return nextStatus === currentStatus ? currentEffectiveDate || '' : '';
}

function contextValue(value) {
    return value === null || value === undefined || value === ''
        ? null
        : String(value);
}

function mutationSignature(payload) {
    return JSON.stringify(payload);
}

export const useResidentsStore = defineStore('residents', {
    state: () => ({
        siteId: null,
        companyId: null,
        userId: null,
        residents: [],
        pagination: {
            page: 1,
            pageSize: DEFAULT_PAGE_SIZE,
            totalItems: 0,
            totalPages: 0,
        },
        selectedResident: null,
        history: [],
        historyPagination: null,
        cases: [],
        casesPagination: null,
        stewardshipCases: [],
        stewardshipCasesPagination: null,
        activity: [],
        activitySources: [],
        activityPagination: null,
        loading: false,
        detailLoading: false,
        error: null,
        staleConflict: null,
        contextGeneration: 0,
        listRequestId: 0,
        completeRequestId: 0,
        detailRequestId: 0,
        historyRequestId: 0,
        casesRequestId: 0,
        stewardshipCasesRequestId: 0,
        activityRequestId: 0,
        mutationIntents: {},
    }),
    actions: {
        client() {
            return createApiV2Client({
                apiRoot: residentConfig.apiRoot,
                fetchImpl: residentConfig.fetchImpl || globalThis.fetch,
            });
        },
        residentsPath(residentId = null, suffix = '') {
            if (!this.siteId)
                throw new Error('A current authorized site is required.');
            const base = `/sites/${this.siteId}/residents`;
            return residentId ? `${base}/${residentId}${suffix}` : base;
        },
        setContext({ siteId, companyId, userId }) {
            const next = {
                siteId: contextValue(siteId),
                companyId: contextValue(companyId),
                userId: contextValue(userId),
            };
            const changed =
                next.siteId !== this.siteId ||
                next.companyId !== this.companyId ||
                next.userId !== this.userId;
            if (!changed) return false;
            this.contextGeneration += 1;
            this.invalidateRequests();
            this.siteId = next.siteId;
            this.companyId = next.companyId;
            this.userId = next.userId;
            this.residents = [];
            this.pagination = {
                page: 1,
                pageSize: DEFAULT_PAGE_SIZE,
                totalItems: 0,
                totalPages: 0,
            };
            this.selectedResident = null;
            this.history = [];
            this.historyPagination = null;
            this.cases = [];
            this.casesPagination = null;
            this.stewardshipCases = [];
            this.stewardshipCasesPagination = null;
            this.activity = [];
            this.activitySources = [];
            this.activityPagination = null;
            this.error = null;
            this.staleConflict = null;
            this.mutationIntents = {};
            this.loading = false;
            this.detailLoading = false;
            return true;
        },
        invalidateRequests() {
            this.listRequestId += 1;
            this.completeRequestId += 1;
            this.detailRequestId += 1;
            this.historyRequestId += 1;
            this.casesRequestId += 1;
            this.stewardshipCasesRequestId += 1;
            this.activityRequestId += 1;
        },
        clearContext() {
            this.setContext({ siteId: null, companyId: null, userId: null });
        },
        async listResidents(query = {}) {
            if (!this.siteId) return null;
            const normalized = normalizeResidentQuery(query);
            const contextGeneration = this.contextGeneration;
            const requestId = ++this.listRequestId;
            const siteId = this.siteId;
            this.loading = true;
            this.error = null;
            try {
                const result = await this.client().request(
                    this.residentsPath(),
                    {
                        query: normalized,
                    }
                );
                if (
                    requestId !== this.listRequestId ||
                    contextGeneration !== this.contextGeneration ||
                    siteId !== this.siteId
                ) {
                    return null;
                }
                this.residents = result.items || [];
                this.pagination = result.pagination || this.pagination;
                return result;
            } catch (error) {
                if (
                    requestId === this.listRequestId &&
                    contextGeneration === this.contextGeneration &&
                    siteId === this.siteId
                ) {
                    this.error = error;
                }
                throw error;
            } finally {
                if (requestId === this.listRequestId) this.loading = false;
            }
        },
        async listCompleteResidents(query = {}) {
            if (!this.siteId) return [];
            const normalized = normalizeResidentQuery({
                ...query,
                page: 1,
                pageSize: COMPLETE_PAGE_SIZE,
            });
            const contextGeneration = this.contextGeneration;
            const requestId = ++this.completeRequestId;
            const siteId = this.siteId;
            const items = [];
            let page = 1;
            let totalPages = 1;
            do {
                const result = await this.client().request(
                    this.residentsPath(),
                    {
                        query: { ...normalized, page },
                    }
                );
                if (
                    requestId !== this.completeRequestId ||
                    contextGeneration !== this.contextGeneration ||
                    siteId !== this.siteId
                ) {
                    return [];
                }
                items.push(...(result.items || []));
                totalPages = Number(result.pagination?.totalPages || 1);
                page += 1;
            } while (page <= totalPages);
            return items;
        },
        async loadResident(residentId) {
            const contextGeneration = this.contextGeneration;
            const requestId = ++this.detailRequestId;
            const siteId = this.siteId;
            if (
                String(this.selectedResident?.id || '') !== String(residentId)
            ) {
                this.historyRequestId += 1;
                this.casesRequestId += 1;
                this.stewardshipCasesRequestId += 1;
                this.activityRequestId += 1;
                this.selectedResident = null;
            }
            this.history = [];
            this.historyPagination = null;
            this.cases = [];
            this.casesPagination = null;
            this.stewardshipCases = [];
            this.stewardshipCasesPagination = null;
            this.activity = [];
            this.activitySources = [];
            this.activityPagination = null;
            this.detailLoading = true;
            try {
                const result = await this.client().request(
                    this.residentsPath(residentId)
                );
                if (
                    requestId !== this.detailRequestId ||
                    contextGeneration !== this.contextGeneration ||
                    siteId !== this.siteId
                ) {
                    return null;
                }
                this.selectedResident = result;
                this.history = [];
                this.cases = [];
                this.stewardshipCases = [];
                this.activity = [];
                this.staleConflict = null;
                return result;
            } finally {
                if (requestId === this.detailRequestId)
                    this.detailLoading = false;
            }
        },
        async loadHistory(residentId, page = 1) {
            const contextGeneration = this.contextGeneration;
            const requestId = ++this.historyRequestId;
            const selectedResidentId = this.selectedResident?.id;
            const result = await this.client().request(
                this.residentsPath(residentId, '/history'),
                { query: { page, pageSize: DEFAULT_PAGE_SIZE } }
            );
            if (
                requestId !== this.historyRequestId ||
                contextGeneration !== this.contextGeneration ||
                String(selectedResidentId || '') !==
                    String(this.selectedResident?.id || '') ||
                String(residentId) !== String(this.selectedResident?.id || '')
            ) {
                return null;
            }
            this.history = result.items || [];
            this.historyPagination = result.pagination || null;
            return result;
        },
        async loadCases(residentId, page = 1) {
            const contextGeneration = this.contextGeneration;
            const requestId = ++this.casesRequestId;
            const selectedResidentId = this.selectedResident?.id;
            const result = await this.client().request(
                this.residentsPath(residentId, '/cases'),
                { query: { page, pageSize: DEFAULT_PAGE_SIZE } }
            );
            if (
                requestId !== this.casesRequestId ||
                contextGeneration !== this.contextGeneration ||
                String(selectedResidentId || '') !==
                    String(this.selectedResident?.id || '') ||
                String(residentId) !== String(this.selectedResident?.id || '')
            ) {
                return null;
            }
            this.cases = result.items || [];
            this.casesPagination = result.pagination || null;
            return result;
        },
        async loadStewardshipCases(residentId, page = 1) {
            const contextGeneration = this.contextGeneration;
            const requestId = ++this.stewardshipCasesRequestId;
            const selectedResidentId = this.selectedResident?.id;
            const result = await this.client().request(
                this.residentsPath(residentId, '/stewardship-cases'),
                { query: { page, pageSize: DEFAULT_PAGE_SIZE } }
            );
            if (
                requestId !== this.stewardshipCasesRequestId ||
                contextGeneration !== this.contextGeneration ||
                String(selectedResidentId || '') !==
                    String(this.selectedResident?.id || '') ||
                String(residentId) !== String(this.selectedResident?.id || '')
            ) {
                return null;
            }
            this.stewardshipCases = result.items || [];
            this.stewardshipCasesPagination = result.pagination || null;
            return result;
        },
        async loadActivity(
            residentId,
            { cursor = null, source = null, append = false } = {}
        ) {
            const contextGeneration = this.contextGeneration;
            const requestId = ++this.activityRequestId;
            const selectedResidentId = this.selectedResident?.id;
            const result = await this.client().request(
                this.residentsPath(residentId, '/activity'),
                {
                    query: {
                        pageSize: DEFAULT_PAGE_SIZE,
                        ...(cursor ? { cursor } : {}),
                        ...(source ? { source } : {}),
                    },
                }
            );
            if (
                requestId !== this.activityRequestId ||
                contextGeneration !== this.contextGeneration ||
                String(selectedResidentId || '') !==
                    String(this.selectedResident?.id || '') ||
                String(residentId) !== String(this.selectedResident?.id || '')
            ) {
                return null;
            }
            this.activity = append
                ? [...this.activity, ...(result.items || [])]
                : result.items || [];
            this.activitySources = result.availableSources || [];
            this.activityPagination = result.pagination || null;
            return result;
        },
        mutationOptions(intent, payload, version) {
            const signature = mutationSignature({
                payload,
                version: version ?? null,
            });
            const existing = this.mutationIntents[intent];
            const key =
                existing?.signature === signature
                    ? existing.key
                    : residentConfig.idempotencyKeyFactory();
            this.mutationIntents[intent] = { signature, key };
            return {
                body: payload,
                idempotencyKey: key,
                ...(version == null ? {} : { ifMatch: quotedVersion(version) }),
            };
        },
        finishMutation(intent, resident, contextGeneration, idempotencyKey) {
            if (this.mutationIntents[intent]?.key === idempotencyKey) {
                delete this.mutationIntents[intent];
            }
            if (contextGeneration !== this.contextGeneration) return null;
            this.staleConflict = null;
            this.selectedResident = resident;
            return resident;
        },
        handleMutationError(error, contextGeneration) {
            if (
                contextGeneration === this.contextGeneration &&
                error?.status === 409 &&
                error?.code === 'VERSION_CONFLICT'
            ) {
                this.staleConflict = {
                    currentVersion: error.details?.currentVersion || null,
                    message: error.message,
                };
            }
            throw error;
        },
        async createResident(payload) {
            const intent = 'create';
            const contextGeneration = this.contextGeneration;
            const options = this.mutationOptions(intent, payload);
            try {
                const resident = await this.client().request(
                    this.residentsPath(),
                    {
                        method: 'POST',
                        ...options,
                    }
                );
                return this.finishMutation(
                    intent,
                    resident,
                    contextGeneration,
                    options.idempotencyKey
                );
            } catch (error) {
                if (contextGeneration !== this.contextGeneration) return null;
                return this.handleMutationError(error, contextGeneration);
            }
        },
        async patchResident(residentId, version, payload) {
            const intent = `patch:${residentId}`;
            const contextGeneration = this.contextGeneration;
            const options = this.mutationOptions(intent, payload, version);
            try {
                const resident = await this.client().request(
                    this.residentsPath(residentId),
                    {
                        method: 'PATCH',
                        ...options,
                    }
                );
                return this.finishMutation(
                    intent,
                    resident,
                    contextGeneration,
                    options.idempotencyKey
                );
            } catch (error) {
                if (contextGeneration !== this.contextGeneration) return null;
                return this.handleMutationError(error, contextGeneration);
            }
        },
        async changeStatus(residentId, version, payload) {
            const intent = `status:${residentId}`;
            const contextGeneration = this.contextGeneration;
            const options = this.mutationOptions(intent, payload, version);
            try {
                const resident = await this.client().request(
                    this.residentsPath(residentId, '/status'),
                    {
                        method: 'PUT',
                        ...options,
                    }
                );
                return this.finishMutation(
                    intent,
                    resident,
                    contextGeneration,
                    options.idempotencyKey
                );
            } catch (error) {
                if (contextGeneration !== this.contextGeneration) return null;
                return this.handleMutationError(error, contextGeneration);
            }
        },
    },
});
